import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ICBSCustomerDetails } from 'Models/BOB/ResponseModels'
import { ISwitchOption } from 'Models/Common'
import { IExistingPolicyFormInput } from 'Models/RequestModels'
import { IGetCustomerCkycResponse } from 'Models/RequestModels/Life/CKyc'
import { IAssuredDetailsForm } from 'Pages/Life/SuitabilityMatrixSteps/AssuredDetails/AssuredDetailsForm'

export type TDisabilityConsentStatus = 'APPROVED' | 'INVALID' | 'NOT_CREATED' | 'PENDING'

export interface IAssuredAndPayorDetailsDataStoreValues {
  assured_personalDetails?: Record<string, string | boolean | number>
  assured_disabilityDetails?: Record<string, string | boolean | number>
  assured_disabilityConsentId?: string
  assured_disabilityConsentStatus?: TDisabilityConsentStatus
  
  assured_educationalDetails?: Record<string, string | number | boolean | string[]>
  // assured_doYouUnderstand: 'english' | 'vernacular' | 'illterate'
  assured_vernacularAndIllterateDetails?: Record<string, string | boolean | number>
  assured_haveExistingPolicy: ISwitchOption
  assured_noOfPolicies: number
  assured_existingPolicyData: IExistingPolicyFormInput[]

  payor_personalDetails?: Record<string, string | boolean | number>
  payor_disabilityDetails?: Record<string, string | boolean | number>
  payor_disabilityConsentId?: string
  payor_disabilityConsentStatus?: TDisabilityConsentStatus

  payor_educationalDetails?: Record<string, string | number | boolean | string[]>
  // payor_doYouUnderstand: 'english' | 'vernacular' | 'illterate'
  payor_vernacularAndIllterateDetails?: Record<string, string | boolean | number>
  payor_haveExistingPolicy: ISwitchOption
  payor_noOfPolicies: number
  payor_existingPolicyData: IExistingPolicyFormInput[]

  payorCkycData?: IGetCustomerCkycResponse
  annutityData?: IAssuredDetailsForm

  joint_assured_vernacularAndIllterateDetails?: Record<string, string | boolean | number>
  joint_assured_haveExistingPolicy: ISwitchOption
  joint_assured_noOfPolicies: number
  joint_assured_existingPolicyData: IExistingPolicyFormInput[]
  joint_assured_CkycData?: IGetCustomerCkycResponse
  joint_assured_cbsDetails?: ICBSCustomerDetails
  joint_assured_personalDetails?: Record<string, string | boolean | number>
  joint_assured_disabilityDetails?: Record<string, string | boolean | number>
  joint_assured_disabilityConsentId?: string
  joint_assured_disabilityConsentStatus?: TDisabilityConsentStatus
  joint_assured_educationalDetails?: Record<string, string | boolean | number>
}

const initialState: IAssuredAndPayorDetailsDataStoreValues = {
  // assured_doYouUnderstand: 'english',
  // payor_doYouUnderstand: 'english',
  assured_haveExistingPolicy: {
    label: 'No',
    value: false,
  },
  payor_haveExistingPolicy: {
    label: 'No',
    value: false,
  },
  assured_noOfPolicies: 1,
  payor_noOfPolicies: 1,
  assured_existingPolicyData: [],
  payor_existingPolicyData: [],

  joint_assured_noOfPolicies: 1,
  joint_assured_existingPolicyData: [],
  joint_assured_haveExistingPolicy: {
    label: 'No',
    value: false,
  },
}

const assuredAndPayorDetailsSlice = createSlice({
  name: 'assuredAndPayorDetailsSlice',
  initialState: initialState,
  reducers: {
    updateJointPersonalDetails: (
      state,
      action: PayloadAction<{
        personalDetails: Record<string, string | boolean | number>
      }>,
    ) => {
      const { personalDetails } = action.payload
      state.joint_assured_personalDetails = personalDetails
    },
    updateJointPersonEducationalDetails: (
      state,
      action: PayloadAction<{
        educationalDetails: Record<string, string | boolean | number>
      }>,
    ) => {
      const { educationalDetails } = action.payload
      state.joint_assured_educationalDetails = educationalDetails
    },
    updatePersonalDetails: (
      state,
      action: PayloadAction<{
        personalDetails: Record<string, string | boolean | number>
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { formType, personalDetails } = action.payload
      if (formType === 'ASSURED') {
        state.assured_personalDetails = personalDetails
      } else if (formType === 'JOINT') {
        state.joint_assured_personalDetails = personalDetails
      } else {
        state.payor_personalDetails = personalDetails
      }
    },
    updatePersonDisabilityDetails: (
      state,
      action: PayloadAction<{
        disabilityData: Record<string, string | boolean | number>
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { formType, disabilityData } = action.payload
      if (formType === 'ASSURED') {
        state.assured_disabilityDetails = disabilityData
      } else if (formType === 'JOINT') {
        state.joint_assured_disabilityDetails = disabilityData
      } else {
        state.payor_disabilityDetails = disabilityData
      }
    },
    updateDisabilityConsent: (
      state,
      action: PayloadAction<{
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
        consentId?: string
        consentStatus?: TDisabilityConsentStatus
      }>,
    ) => {
      const { formType, consentId, consentStatus } = action.payload
      if (formType === 'ASSURED') {
        if (consentId !== undefined) {
          state.assured_disabilityConsentId = consentId
        }
        if (consentStatus !== undefined) {
          state.assured_disabilityConsentStatus = consentStatus
        }
      } else if (formType === 'JOINT') {
        if (consentId !== undefined) {
          state.joint_assured_disabilityConsentId = consentId
        }
        if (consentStatus !== undefined) {
          state.joint_assured_disabilityConsentStatus = consentStatus
        }
      } else {
        if (consentId !== undefined) {
          state.payor_disabilityConsentId = consentId
        }
        if (consentStatus !== undefined) {
          state.payor_disabilityConsentStatus = consentStatus
        }
      }
    },
    updateEducationalDetails: (
      state,
      action: PayloadAction<{
        educationalDetails: Record<string, string | boolean | number>
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { educationalDetails, formType } = action.payload
      if (formType === 'ASSURED') {
        state.assured_educationalDetails = educationalDetails
      } else if (formType === 'JOINT') {
        state.joint_assured_educationalDetails = educationalDetails
      } else {
        state.payor_educationalDetails = educationalDetails
      }
    },
    // updateDoYouUnderstand: (
    //   state,
    //   action: PayloadAction<{
    //     doYouUnderstand: 'english' | 'vernacular' | 'illterate'
    //     formType: 'ASSURED' | 'PAYOR'
    //   }>,
    // ) => {
    //   const { doYouUnderstand, formType } = action.payload
    //   if (formType === 'ASSURED') {
    //     state.assured_doYouUnderstand = doYouUnderstand
    //   } else {
    //     state.payor_doYouUnderstand = doYouUnderstand
    //   }
    // },
    updateVernacularAndillterateForm: (
      state,
      action: PayloadAction<{
        vernacularAndillterateData: Record<string, string | boolean | number>
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { formType, vernacularAndillterateData } = action.payload
      if (formType === 'ASSURED') {
        state.assured_vernacularAndIllterateDetails = vernacularAndillterateData
      } else if (formType === 'JOINT') {
        state.joint_assured_vernacularAndIllterateDetails = vernacularAndillterateData
      } else {
        state.payor_vernacularAndIllterateDetails = vernacularAndillterateData
      }
    },

    updateHaveExistingPolicy: (
      state,
      action: PayloadAction<{
        haveExistingPolicy: ISwitchOption
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { formType, haveExistingPolicy } = action.payload
      if (formType === 'ASSURED') {
        state.assured_haveExistingPolicy = haveExistingPolicy
      } else {
        state.payor_haveExistingPolicy = haveExistingPolicy
      }
    },
    updateNoOfPolicies: (
      state,
      action: PayloadAction<{ noOfPolicies: number; formType: 'ASSURED' | 'PAYOR' | 'JOINT' }>,
    ) => {
      const { formType, noOfPolicies } = action.payload
      if (formType === 'ASSURED') {
        state.assured_noOfPolicies = noOfPolicies
      } else if (formType === 'JOINT') {
        state.joint_assured_noOfPolicies = noOfPolicies
      } else {
        state.payor_noOfPolicies = noOfPolicies
      }
    },
    updateExistingPolicyData: (
      state,
      action: PayloadAction<{
        existingPolicyData: IExistingPolicyFormInput[]
        formType: 'ASSURED' | 'PAYOR' | 'JOINT'
      }>,
    ) => {
      const { formType, existingPolicyData } = action.payload
      if (formType === 'ASSURED') {
        state.assured_existingPolicyData = existingPolicyData
      } else if (formType === 'JOINT') {
        state.joint_assured_existingPolicyData = existingPolicyData
      } else {
        state.payor_existingPolicyData = existingPolicyData
      }
    },
    updatePayorCkycData: (state, action: PayloadAction<IGetCustomerCkycResponse>) => {
      state.payorCkycData = action.payload
    },

    updateJointAssuredCBSData: (state, action: PayloadAction<ICBSCustomerDetails | undefined>) => {
      state.joint_assured_cbsDetails = action.payload
    },

    updateAnnuityData: (state, action: PayloadAction<IAssuredDetailsForm | undefined>) => {
      state.annutityData = action.payload
    },

    updateAllAssuredAndPayorData: (
      state,
      action: PayloadAction<IAssuredAndPayorDetailsDataStoreValues>,
    ) => {
      const {
        assured_personalDetails,
        assured_disabilityDetails,
        assured_disabilityConsentId,
        assured_disabilityConsentStatus,
        assured_educationalDetails,
        // assured_doYouUnderstand,
        assured_vernacularAndIllterateDetails,
        assured_haveExistingPolicy,
        assured_noOfPolicies,
        assured_existingPolicyData,
        payor_personalDetails,
        payor_disabilityDetails,
        payor_disabilityConsentId,
        payor_disabilityConsentStatus,
        payor_educationalDetails,
        // payor_doYouUnderstand,
        payor_vernacularAndIllterateDetails,
        payor_haveExistingPolicy,
        payor_noOfPolicies,
        payor_existingPolicyData,
        payorCkycData,
        joint_assured_cbsDetails,
        annutityData,
        joint_assured_educationalDetails,
        joint_assured_personalDetails,
      } = action.payload

      if (assured_personalDetails !== undefined) {
        state.assured_personalDetails = assured_personalDetails
      }
      if (assured_disabilityDetails !== undefined) {
        state.assured_disabilityDetails = assured_disabilityDetails
      }
      if (assured_disabilityConsentId !== undefined) {
        state.assured_disabilityConsentId = assured_disabilityConsentId
      }
      if (assured_disabilityConsentStatus !== undefined) {
        state.assured_disabilityConsentStatus = assured_disabilityConsentStatus
      }
      if (assured_educationalDetails !== undefined) {
        state.assured_educationalDetails = assured_educationalDetails
      }
      // if (assured_doYouUnderstand !== undefined) {
      //   state.assured_doYouUnderstand = assured_doYouUnderstand
      // }
      if (assured_vernacularAndIllterateDetails !== undefined) {
        state.assured_vernacularAndIllterateDetails = assured_vernacularAndIllterateDetails
      }
      if (assured_haveExistingPolicy !== undefined) {
        state.assured_haveExistingPolicy = assured_haveExistingPolicy
      }
      if (assured_noOfPolicies !== undefined) {
        state.assured_noOfPolicies = assured_noOfPolicies
      }
      if (assured_existingPolicyData !== undefined) {
        state.assured_existingPolicyData = assured_existingPolicyData
      }

      if (payor_personalDetails !== undefined) {
        state.payor_personalDetails = payor_personalDetails
      }
      if (payor_disabilityDetails !== undefined) {
        state.payor_disabilityDetails = payor_disabilityDetails
      }
      if (payor_disabilityConsentId !== undefined) {
        state.payor_disabilityConsentId = payor_disabilityConsentId
      }
      if (payor_disabilityConsentStatus !== undefined) {
        state.payor_disabilityConsentStatus = payor_disabilityConsentStatus
      }
      if (payor_educationalDetails !== undefined) {
        state.payor_educationalDetails = payor_educationalDetails
      }
      // if (payor_doYouUnderstand !== undefined) {
      //   state.payor_doYouUnderstand = payor_doYouUnderstand
      // }
      if (payor_vernacularAndIllterateDetails !== undefined) {
        state.payor_vernacularAndIllterateDetails = payor_vernacularAndIllterateDetails
      }
      if (payor_haveExistingPolicy !== undefined) {
        state.payor_haveExistingPolicy = payor_haveExistingPolicy
      }
      if (payor_noOfPolicies !== undefined) {
        state.payor_noOfPolicies = payor_noOfPolicies
      }
      if (payor_existingPolicyData !== undefined) {
        state.payor_existingPolicyData = payor_existingPolicyData
      }
      if (payorCkycData !== undefined) {
        state.payorCkycData = payorCkycData
      }
      if (joint_assured_cbsDetails !== undefined) {
        state.joint_assured_cbsDetails = joint_assured_cbsDetails
      }
      if (annutityData !== undefined) {
        state.annutityData = annutityData
      }
      if (joint_assured_educationalDetails !== undefined) {
        state.joint_assured_educationalDetails = joint_assured_educationalDetails
      }
      if (joint_assured_personalDetails !== undefined) {
        state.joint_assured_personalDetails = joint_assured_personalDetails
      }
    },
    resetAllAssuredAndPayorDetails: () => initialState,
  },
})

const { reducer } = assuredAndPayorDetailsSlice

const {
  updatePersonalDetails,
  updateEducationalDetails,
  updatePersonDisabilityDetails,
  updateDisabilityConsent,
  // updateDoYouUnderstand,
  updateVernacularAndillterateForm,
  updateHaveExistingPolicy,
  updateNoOfPolicies,
  updateExistingPolicyData,
  updatePayorCkycData,
  resetAllAssuredAndPayorDetails,
  updateAllAssuredAndPayorData,
  updateJointAssuredCBSData,
  updateAnnuityData,
  updateJointPersonalDetails,
  updateJointPersonEducationalDetails,
} = assuredAndPayorDetailsSlice.actions

export {
  reducer as assuredAndPayorDetailsReducer,
  updatePersonalDetails,
  updatePersonDisabilityDetails,
  updateDisabilityConsent,
  updateEducationalDetails,
  // updateDoYouUnderstand,
  updateVernacularAndillterateForm,
  updateHaveExistingPolicy,
  updateNoOfPolicies,
  updateExistingPolicyData,
  updatePayorCkycData,
  resetAllAssuredAndPayorDetails,
  updateAllAssuredAndPayorData,
  updateJointAssuredCBSData,
  updateAnnuityData,
  updateJointPersonalDetails,
  updateJointPersonEducationalDetails,
}
